import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import { useUploadCertification } from '../../hooks/useUploadCertification';
import type { SupplierCertification } from '../../types/types';

interface SupplierProfileBuilderCertificationsProps {
  supplierId: string;
  onCountChange?: (count: number) => void;
}

const emptyForm = {
  certificate_name: '',
  issuing_body: '',
  issue_date: '',
  valid_until: '',
  certificate_file_url: '',
};

export function SupplierProfileBuilderCertifications({
  supplierId,
  onCountChange,
}: SupplierProfileBuilderCertificationsProps) {
  const [certifications, setCertifications] = useState<SupplierCertification[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState(emptyForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const {
    uploadCertification,
    uploading,
    error: uploadError,
  } = useUploadCertification();

  const documentBucket = useMemo(() => 'supplier-profile-assets', []);

  const fetchCertifications = async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('supplier_certifications')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      captureSupabaseError(fetchError, { op: 'certifications.fetch' });
      setError(fetchError.message);
      setCertifications([]);
    } else {
      const certs = (data ?? []) as SupplierCertification[];
      const enriched = await Promise.all(
        certs.map(async (certification) => {
          if (!certification.certificate_file_url) return certification;
          if (certification.certificate_file_url.startsWith('http')) {
            return {
              ...certification,
              signed_url: certification.certificate_file_url,
            };
          }
          const { data: urlData, error: urlError } = await supabase.storage
            .from(documentBucket)
            .createSignedUrl(certification.certificate_file_url, 60 * 5);
          if (urlError) {
            captureSupabaseError(urlError, {
              op: 'certifications.signed-url',
            });
          }
          return {
            ...certification,
            signed_url: urlData?.signedUrl ?? null,
          };
        })
      );
      setCertifications(enriched);
      onCountChange?.(enriched.length);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchCertifications();
  }, [supplierId]);

  useEffect(() => {
    return () => {
      // Cleanup any selected file when component unmounts
      setSelectedFile(null);
      console.log(
        'Certifications component unmounted, cleaned up selected file'
      );
    };
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedFile(null);
    setEditingId(null);
    setFormErrors({});
    setFileInputKey((prev) => prev + 1);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const getCertificateUrl = (value: string | null) => {
    if (!value) return null;
    if (value.startsWith('http')) return value;
    const { data } = supabase.storage.from(documentBucket).getPublicUrl('');
    const baseUrl = data.publicUrl.replace(/\/$/, '');
    const encodedPath = value
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    return `${baseUrl}/${encodedPath}`;
  };

  const handleDelete = async (certification: SupplierCertification) => {
    if (!confirm('Delete this certification?')) return;

    setSaving(true);
    setError(null);

    if (
      certification.certificate_file_url &&
      !certification.certificate_file_url.startsWith('http')
    ) {
      const { error: removeError } = await supabase.storage
        .from(documentBucket)
        .remove([certification.certificate_file_url]);

      if (removeError) {
        captureSupabaseError(removeError, { op: 'certifications.file.delete' });
        setError(removeError.message);
      }
    }

    const { error: deleteError } = await supabase
      .from('supplier_certifications')
      .delete()
      .eq('id', certification.id);

    if (deleteError) {
      captureSupabaseError(deleteError, { op: 'certifications.delete' });
      setError(deleteError.message);
    } else {
      await fetchCertifications();
    }

    setSaving(false);
  };

  const handleSave = async () => {
    const nextErrors: Record<string, string> = {};
    if (!form.certificate_name.trim()) {
      nextErrors.certificate_name = 'Certificate name is required';
    }
    if (!form.certificate_file_url && !selectedFile) {
      nextErrors.certificate_file_url = 'Certificate file is required';
    }

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return;
    }

    setSaving(true);
    setError(null);
    setFormErrors({});

    let certificateFilePath = form.certificate_file_url || null;

    if (selectedFile) {
      const uploadResult = await uploadCertification(selectedFile, supplierId);
      if (!uploadResult) {
        setSaving(false);
        setError('File upload failed');
        return;
      }
      certificateFilePath = uploadResult.storagePath;
    }

    const payload = {
      supplier_id: supplierId,
      certificate_name: form.certificate_name.trim(),
      issuing_body: form.issuing_body?.trim() || null,
      issue_date: form.issue_date || null,
      valid_until: form.valid_until || null,
      certificate_file_url: certificateFilePath,
      updated_at: new Date().toISOString(),
    };

    const { error: saveError } = editingId
      ? await supabase
          .from('supplier_certifications')
          .update(payload)
          .eq('id', editingId)
      : await supabase.from('supplier_certifications').insert({
          ...payload,
          created_at: new Date().toISOString(),
        });

    if (saveError) {
      captureSupabaseError(saveError, { op: 'certifications.save' });
      setError(saveError.message);
      setSaving(false);
      return;
    }

    resetForm();
    await fetchCertifications();
    setSaving(false);
    setShowModal(false);
  };

  return (
    <div className="py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Certifications</h5>
        <Button variant="primary" onClick={openModal}>
          <i className="bi bi-plus-lg me-2"></i>
          Add certification
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : certifications.length ? (
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              {/* <th>Issuer</th>
              <th>Issue</th>
              <th>Valid until</th> */}
              <th>File</th>
              {/* <th>Status</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {certifications.map((certification) => {
              const publicUrl =
                certification.signed_url ??
                getCertificateUrl(certification.certificate_file_url);

              return (
                <tr key={certification.id}>
                  <td>{certification.certificate_name}</td>
                  {/* <td>{certification.issuing_body || '—'}</td>
                  <td>
                    {certification.issue_date
                      ? new Date(certification.issue_date).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    {certification.valid_until
                      ? new Date(certification.valid_until).toLocaleDateString()
                      : '—'}
                  </td> */}
                  <td>
                    {publicUrl ? (
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary"
                      >
                        Download
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  {/* <td>
                    {certification.is_verified ? (
                      <span className="badge bg-success">Verified</span>
                    ) : (
                      <span className="badge bg-secondary">Pending</span>
                    )}
                  </td> */}
                  <td>
                    <div className="d-flex gap-2">
                      {/* <Button
                        size="sm"
                        variant="outline-primary"
                        className="editor-icon-button"
                        onClick={() => handleEdit(certification)}
                        disabled={saving || uploading}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button> */}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="editor-icon-button"
                        onClick={() => handleDelete(certification)}
                        disabled={saving || uploading}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted">No certifications added yet.</p>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? 'Update certification' : 'Add certification'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Certificate name *</Form.Label>
            <Form.Control
              type="text"
              value={form.certificate_name}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  certificate_name: event.target.value,
                }))
              }
              isInvalid={Boolean(formErrors.certificate_name)}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.certificate_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Issuing body</Form.Label>
            <Form.Control
              type="text"
              value={form.issuing_body}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  issuing_body: event.target.value,
                }))
              }
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Issue date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.issue_date}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      issue_date: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Valid until</Form.Label>
                <Form.Control
                  type="date"
                  value={form.valid_until}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      valid_until: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Upload certificate *</Form.Label>
            <Form.Control
              key={fileInputKey}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              isInvalid={Boolean(formErrors.certificate_file_url)}
              onChange={(event) => {
                // @ts-expect-error ts(2339)
                if (event.target.files?.[0]) {
                  // @ts-expect-error ts(2339)
                  setSelectedFile(event.target.files[0]);
                }
              }}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.certificate_file_url}
            </Form.Control.Feedback>
            {selectedFile && (
              <div className="small text-muted mt-2">
                Selected: {selectedFile.name}
              </div>
            )}
            {uploadError && (
              <Alert variant="danger" className="mt-2">
                {uploadError}
              </Alert>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || uploading}
          >
            {saving || uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : editingId ? (
              'Update certification'
            ) : (
              'Add certification'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
